import {
  LoadSimulationAiQueriesDocument,
  LoadSimulationAiQueriesQuery,
  LoadSimulationAiQueriesQueryVariables,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback } from 'recoil';
import { secureClient } from '../../GraphQLClient';
import {
  aiAgentQueries,
  aiAgentQueriesLoadStatus,
  aiAgentQueriesState,
} from '../../aiAgent/store/aiAgent.state';
import { LoadAiQueriesParamsBase } from '../../aiAgent/store/aiAgent.types';
import { AsyncLoadStatus } from '../../common/types';
import { errorAppender } from '../../store/error.state';

export type LoadSimulationAiQueriesParams = LoadAiQueriesParamsBase & {
  simulationId: string;
};

export function useLoadSimulationAiQueries() {
  const { t } = useTranslation('errors');
  const errorTitle = t`Could not load Simulation Ai Agent Queries`;

  const cleanup = useRecoilCallback(({ set, reset }) => async () => {
    reset(aiAgentQueries);
    reset(aiAgentQueriesLoadStatus);
  });

  const init = useRecoilCallback(
    ({ set, reset }) =>
      async (params: LoadSimulationAiQueriesParams) => {
        if (!params.isAppend) {
          reset(aiAgentQueries);
        }
        set(aiAgentQueriesLoadStatus, AsyncLoadStatus.Loading);
      },
  );

  const handleError = useRecoilCallback(
    ({ snapshot, set }) =>
      async (details: string, stack: string) => {
        set(errorAppender, {
          id: nanoid(),
          title: errorTitle,
          details: details,
          callStack: stack,
        });
        set(aiAgentQueriesLoadStatus, AsyncLoadStatus.Error);
        return;
      },
  );

  const loadAiQueriesCallback = useRecoilCallback(
    ({ snapshot, set }) =>
      async (params: LoadSimulationAiQueriesParams) => {
        try {
          const actualityResponse = await secureClient.query<
            LoadSimulationAiQueriesQuery,
            LoadSimulationAiQueriesQueryVariables
          >({
            fetchPolicy: 'no-cache',
            query: LoadSimulationAiQueriesDocument,
            variables: {
              simulationId: params.simulationId,
              page: {
                limit: params.limit || 20,
                skip: params.skip || 0,
              },
            },
          });

          if (actualityResponse?.errors) {
            handleError(
              null,
              actualityResponse?.errors.map(e => e.message).join('. '),
            );
            return;
          }

          const actuality = actualityResponse.data.simulation;
          if (params.isAppend) {
            set(aiAgentQueries, current => [
              ..._.reverse(actuality.aiQueries.content),
              ...current,
            ]);
          } else {
            set(aiAgentQueries, _.reverse(actuality.aiQueries.content));
          }

          set(aiAgentQueriesState, {
            totalCount: actuality.aiQueries.totalCount,
          });
          // status
          set(aiAgentQueriesLoadStatus, AsyncLoadStatus.Ok);
          return actuality;
        } catch (ex) {
          handleError(ex.message || ex, ex.stack || null);
          return;
        }
      },
  );

  async function load(params: LoadSimulationAiQueriesParams) {
    await init(params);
    await loadAiQueriesCallback(params);
  }

  return [load, /*cancel,*/ cleanup] as const;
}
